'use client'

/**
 * Cart mutation hooks with optimistic updates.
 *
 * Each mutation:
 * 1. Cancels in-flight queries
 * 2. Snapshots current cache
 * 3. Optimistically updates the cache
 * 4. Calls the server action
 * 5. On error: rolls back to snapshot
 * 6. On settle: invalidates queries for fresh data
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { HttpTypes } from '@medusajs/types'
import { cartKeys } from '@lib/query-keys'
import {
  addToCartAction,
  updateCartItemAction,
  removeCartItemAction,
  updateRentalItemAction,
  addRentalToCartAction,
} from '@modules/cart/actions'
import { retrieveCart } from '@lib/data/cart'
import { useTrack } from '@owlcat/app-shell'
import { cartAddedProps } from '@lib/analytics/fleet-events'
import { getClientCartId, setClientCartId } from './use-cart'

type CartData = HttpTypes.StoreCart | null | undefined

function useCartId(): string | null {
  return getClientCartId()
}

// ── Helper: build an Error that carries an errorCode field so the UI
// can render a translated message (cart.errors.<code>) instead of raw
// English text from Medusa core. Used by every mutation hook below. ─────
function makeError(
  message: string,
  errorCode?: string,
  errorParams?: Record<string, string | number>
): Error {
  const err = new Error(message) as Error & {
    errorCode?: string
    errorParams?: Record<string, string | number>
  }
  if (errorCode) err.errorCode = errorCode
  if (errorParams) err.errorParams = errorParams
  return err
}

// ── Helper: optimistic cache update ─────────────────────────────────────────

async function prepareOptimisticUpdate(
  queryClient: ReturnType<typeof useQueryClient>,
  cartId: string | null
) {
  if (!cartId) return { previousCart: undefined }

  // Cancel any outgoing refetches
  await queryClient.cancelQueries({ queryKey: cartKeys.detail(cartId) })

  // Snapshot current value
  const previousCart = queryClient.getQueryData<CartData>(
    cartKeys.detail(cartId)
  )

  return { previousCart }
}

function rollback(
  queryClient: ReturnType<typeof useQueryClient>,
  cartId: string | null,
  previousCart: CartData
) {
  if (cartId && previousCart !== undefined) {
    queryClient.setQueryData(cartKeys.detail(cartId), previousCart)
  }
}

function settle(
  queryClient: ReturnType<typeof useQueryClient>,
  cartId: string | null
) {
  if (cartId) {
    queryClient.invalidateQueries({ queryKey: cartKeys.detail(cartId) })
    queryClient.invalidateQueries({ queryKey: cartKeys.summary(cartId) })
  }
}

// ── useAddToCart ─────────────────────────────────────────────────────────────

export function useAddToCart() {
  const queryClient = useQueryClient()
  const cartId = useCartId()
  const track = useTrack()

  return useMutation({
    mutationFn: async (params: {
      variantId: string
      quantity: number
      countryCode: string
    }) => {
      const result = await addToCartAction(params)
      if (!result.success) {
        throw makeError(result.error || 'Failed to add item to cart', result.errorCode, result.errorParams)
      }
      return result.cart as HttpTypes.StoreCart
    },

    onMutate: async (params) => {
      const { previousCart } = await prepareOptimisticUpdate(queryClient, cartId)

      // Optimistically add a placeholder item
      if (cartId && previousCart) {
        const optimisticItem: Partial<HttpTypes.StoreCartLineItem> = {
          id: `optimistic-${Date.now()}`,
          variant_id: params.variantId,
          quantity: params.quantity,
          title: 'Adding...',
          product_title: 'Adding...',
          unit_price: 0,
          total: 0,
        }

        const optimisticCart = {
          ...previousCart,
          items: [
            ...(previousCart.items || []),
            optimisticItem as HttpTypes.StoreCartLineItem,
          ],
        }

        queryClient.setQueryData(cartKeys.detail(cartId), optimisticCart)
      }

      return { previousCart }
    },

    onError: (_err, _params, context) => {
      rollback(queryClient, cartId, context?.previousCart)
    },

    onSuccess: (cart, params) => {
      // Update cache with real server response
      if (cart?.id) {
        setClientCartId(cart.id)
        queryClient.setQueryData(cartKeys.detail(cart.id), cart)
      }
      track('cart_added', cartAddedProps(cart, params.variantId, params.quantity))
    },

    onSettled: () => {
      settle(queryClient, cartId)
    },
  })
}

// ── useRemoveFromCart ────────────────────────────────────────────────────────

export function useRemoveFromCart() {
  const queryClient = useQueryClient()
  const cartId = useCartId()

  return useMutation({
    mutationFn: async (lineId: string) => {
      const result = await removeCartItemAction(lineId)
      if (!result.success) {
        throw makeError(result.error || 'Failed to remove item', result.errorCode, result.errorParams)
      }
      return result.cart as HttpTypes.StoreCart
    },

    onMutate: async (lineId) => {
      const { previousCart } = await prepareOptimisticUpdate(queryClient, cartId)

      // Optimistically remove the item
      if (cartId && previousCart) {
        const optimisticCart = {
          ...previousCart,
          items: (previousCart.items || []).filter(
            (item) => item.id !== lineId
          ),
        }

        queryClient.setQueryData(cartKeys.detail(cartId), optimisticCart)
      }

      return { previousCart }
    },

    onError: (_err, _lineId, context) => {
      rollback(queryClient, cartId, context?.previousCart)
    },

    onSuccess: (cart) => {
      if (cart?.id) {
        queryClient.setQueryData(cartKeys.detail(cart.id), cart)
      }
    },

    onSettled: () => {
      settle(queryClient, cartId)
    },
  })
}

// ── useUpdateQuantity ────────────────────────────────────────────────────────

export function useUpdateQuantity() {
  const queryClient = useQueryClient()
  const cartId = useCartId()

  return useMutation({
    mutationFn: async (params: { lineId: string; quantity: number }) => {
      const result = await updateCartItemAction(params)
      if (!result.success) {
        throw makeError(result.error || 'Failed to update quantity', result.errorCode, result.errorParams)
      }
      return result.cart as HttpTypes.StoreCart
    },

    onMutate: async (params) => {
      const { previousCart } = await prepareOptimisticUpdate(queryClient, cartId)

      // Optimistically update the quantity
      if (cartId && previousCart) {
        const optimisticCart = {
          ...previousCart,
          items: (previousCart.items || []).map((item) =>
            item.id === params.lineId
              ? { ...item, quantity: params.quantity }
              : item
          ),
        }

        queryClient.setQueryData(cartKeys.detail(cartId), optimisticCart)
      }

      return { previousCart }
    },

    onError: (_err, _params, context) => {
      rollback(queryClient, cartId, context?.previousCart)
    },

    onSuccess: (cart) => {
      if (cart?.id) {
        queryClient.setQueryData(cartKeys.detail(cart.id), cart)
      }
    },

    onSettled: () => {
      settle(queryClient, cartId)
    },
  })
}

// ── useAddRentalToCart ────────────────────────────────────────────────────────

export function useAddRentalToCart() {
  const queryClient = useQueryClient()
  const cartId = useCartId()
  const track = useTrack()

  return useMutation({
    mutationFn: async (params: {
      variantId: string
      rentalPeriod: number
      rentalUnit: string
      startDate: string
      quantity: number
      countryCode: string
    }) => {
      const result = await addRentalToCartAction(params)
      if (!result.success || !result.cart) {
        throw makeError(result.error || 'Failed to add rental to cart', result.errorCode, result.errorParams)
      }
      return result.cart as HttpTypes.StoreCart
    },

    onMutate: async (params) => {
      const { previousCart } = await prepareOptimisticUpdate(queryClient, cartId)

      // Optimistically add a rental placeholder with pending price
      if (cartId && previousCart) {
        const optimisticItem: Partial<HttpTypes.StoreCartLineItem> = {
          id: `optimistic-rental-${Date.now()}`,
          variant_id: params.variantId,
          quantity: params.quantity,
          title: 'Adding rental...',
          product_title: 'Adding rental...',
          unit_price: 0,
          total: 0,
          metadata: {
            isRental: true,
            startDate: params.startDate,
            rentalPeriod: params.rentalPeriod,
            rentalUnit: params.rentalUnit,
            _pending: true,
          },
        }

        const optimisticCart = {
          ...previousCart,
          items: [
            ...(previousCart.items || []),
            optimisticItem as HttpTypes.StoreCartLineItem,
          ],
        }

        queryClient.setQueryData(cartKeys.detail(cartId), optimisticCart)
      }

      return { previousCart }
    },

    onError: (_err, _params, context) => {
      rollback(queryClient, cartId, context?.previousCart)
    },

    onSuccess: async (cart, params) => {
      track('cart_added', cartAddedProps(cart, params.variantId, params.quantity))
      if (cart?.id) {
        setClientCartId(cart.id)
        // The /rental API response carries line items with total=0 (rental
        // pricing isn't computed in that payload). Caching it directly made
        // the mini-cart show CZK 0.00 while the full cart page (which calls
        // retrieveCart) showed the real total. Re-fetch the computed cart so
        // both read the same totals.
        try {
          const fresh = await retrieveCart()
          queryClient.setQueryData(cartKeys.detail(cart.id), fresh ?? cart)
        } catch {
          queryClient.setQueryData(cartKeys.detail(cart.id), cart)
        }
      }
    },

    onSettled: () => {
      settle(queryClient, cartId)
    },
  })
}

// ── useUpdateRentalItem ──────────────────────────────────────────────────────

export function useUpdateRentalItem() {
  const queryClient = useQueryClient()
  const cartId = useCartId()

  return useMutation({
    mutationFn: async (params: {
      lineId: string
      rentalPeriod?: number
      rentalUnit?: 'day' | 'week' | 'month'
      startDate?: string
    }) => {
      const result = await updateRentalItemAction(params)
      if (!result.success) {
        throw makeError(result.error || 'Failed to update rental item', result.errorCode, result.errorParams)
      }
      return result.cart as HttpTypes.StoreCart
    },

    onMutate: async (params) => {
      const { previousCart } = await prepareOptimisticUpdate(queryClient, cartId)

      // Optimistically update rental metadata
      if (cartId && previousCart) {
        const optimisticCart = {
          ...previousCart,
          items: (previousCart.items || []).map((item) => {
            if (item.id !== params.lineId) return item
            const currentMeta = (item.metadata || {}) as Record<string, unknown>
            return {
              ...item,
              metadata: {
                ...currentMeta,
                ...(params.startDate && { startDate: params.startDate }),
                ...(params.rentalPeriod && { rentalPeriod: params.rentalPeriod }),
                ...(params.rentalUnit && { rentalUnit: params.rentalUnit }),
                _pending: true,
              },
            }
          }),
        }

        queryClient.setQueryData(cartKeys.detail(cartId), optimisticCart)
      }

      return { previousCart }
    },

    onError: (_err, _params, context) => {
      rollback(queryClient, cartId, context?.previousCart)
    },

    onSuccess: async (cart) => {
      if (cart?.id) {
        // Same as add-rental: the /rental API payload has uncomputed line
        // totals; re-fetch the computed cart so the mini-cart total matches
        // the full cart page.
        try {
          const fresh = await retrieveCart()
          queryClient.setQueryData(cartKeys.detail(cart.id), fresh ?? cart)
        } catch {
          queryClient.setQueryData(cartKeys.detail(cart.id), cart)
        }
      }
    },

    onSettled: () => {
      settle(queryClient, cartId)
    },
  })
}
